import React from 'react';

// The JD layout (schema 2 — the API maps older versions); the review page uses the same section views
export const JD_SECTIONS = [
  { key: 'focus_tags', label: 'Focus', kind: 'tags' }, { key: 'summary', label: 'Job summary', kind: 'paragraphs' },
  { key: 'responsibilities', label: 'Key responsibilities', kind: 'groups' },
  { key: 'required', label: 'Required qualifications', kind: 'list' }, { key: 'preferred', label: 'Preferred qualifications', kind: 'list' },
  { key: 'skills_table', label: 'Technical skills summary', kind: 'table' },
  { key: 'locked', label: 'Location, work mode & compensation', kind: 'locked' }, { key: 'eeo', label: 'Equal opportunity', kind: 'text' },
];

export function SectionView({ section, value }) {
  const v = value;
  if (section.kind === 'tags') {
    return (
      <div className="flex flex-wrap gap-2">
        {(v || []).map((t) => (
          <span key={t.label + t.value} className="px-3 py-1 rounded-full bg-blue-50 text-brand-blue text-xs font-semibold">
            {t.label}: {t.value}
          </span>
        ))}
      </div>
    );
  }
  if (section.kind === 'paragraphs') return <div className="space-y-2">{(v || []).map((p) => <p key={p} className="text-sm text-gray-700">{p}</p>)}</div>;
  if (section.kind === 'groups') {
    return (
      <div className="space-y-3">
        {(v || []).map((g) => (
          <div key={g.heading}>
            <p className="text-sm font-bold text-gray-900">{g.heading}</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">{g.bullets.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        ))}
      </div>
    );
  }
  if (section.kind === 'table') {
    return (
      <table className="w-full text-sm" data-testid="skills-table">
        <tbody>
          {(v || []).map((r) => (
            <tr key={r.category + r.skills} className="border-t border-gray-100">
              <td className="py-1.5 pr-4 font-semibold text-gray-900 w-1/3">{r.category}</td>
              <td className="py-1.5 text-gray-700">{r.skills}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (section.kind === 'locked') {
    return (
      <div className="text-sm text-gray-700 space-y-1">
        {[(v || {}).line, (v || {}).work_authorization, (v || {}).interview ? `Interview process: ${v.interview}` : null]
          .filter(Boolean).map((l) => <p key={l}>{l}</p>)}
      </div>
    );
  }
  if (section.kind === 'list') return <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">{(v || []).map((x) => <li key={x}>{x}</li>)}</ul>;
  return <p className="text-xs text-gray-500">{v}</p>;
}

const Block = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">{title}</h3>
    {children}
  </div>
);
const Bullets = ({ items }) => (
  <ul className="list-disc list-inside space-y-1 text-gray-800">{(items || []).map((x) => <li key={x}>{x}</li>)}</ul>
);

// Recruiter screen tabs for a job the hiring manager created with Jessica: the approved JD and the
// recruiter brief (recruiter-only — the API refuses hiring managers).
function JobRecruiterTabs({ tab, view, error }) {
  if (error) return <div className="p-8 text-gray-500">{error}</div>;
  if (!view) return <div className="p-8 text-gray-500">Loading…</div>;
  if (tab === 'jd') {
    const jd = view.jd || {};
    return (
      <div className="p-8" data-testid="tab-jd">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{view.req_id ? `${view.req_id} – ` : ''}{view.job_title} <span className="text-sm text-gray-400">v{view.version_no}</span></h2>
        {JD_SECTIONS.map((s) => (jd[s.key] && (Array.isArray(jd[s.key]) ? jd[s.key].length > 0 : true)) && (
          <Block key={s.key} title={s.label}><SectionView section={s} value={jd[s.key]} /></Block>
        ))}
      </div>
    );
  }
  const b = view.recruiter_brief || {};
  return (
    <div className="p-8" data-testid="tab-brief">
      <h2 className="text-2xl font-bold text-gray-900 mb-1">Recruiter brief</h2>
      <p className="text-xs text-gray-500 mb-6">For the recruiting team only — not shown to the hiring manager.</p>
      {b.summary && <p className="text-gray-800 mb-6">{b.summary}</p>}
      {b.market_checks && b.market_checks.length > 0 && <Block title="Market checks"><Bullets items={b.market_checks} /></Block>}
      {b.knockouts && b.knockouts.length > 0 && <Block title="Knock-outs"><Bullets items={b.knockouts} /></Block>}
      {b.selling_points && b.selling_points.length > 0 && <Block title="Selling points"><Bullets items={b.selling_points} /></Block>}
      {b.target_companies && b.target_companies.length > 0 && <Block title="Target companies"><Bullets items={b.target_companies} /></Block>}
      {b.avoid_companies && b.avoid_companies.length > 0 && <Block title="Companies to avoid"><Bullets items={b.avoid_companies} /></Block>}
      {b.boolean_strings && b.boolean_strings.length > 0 && (
        <Block title="Boolean strings">
          {b.boolean_strings.map((s) => (
            <div key={s.label + s.query} className="mb-3">
              <p className="text-xs font-semibold text-gray-600">{s.label}</p>
              <code className="block text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 break-words">{s.query}</code>
            </div>
          ))}
        </Block>
      )}
      {b.sourcing_notes && b.sourcing_notes.length > 0 && <Block title="Sourcing notes"><Bullets items={b.sourcing_notes} /></Block>}
    </div>
  );
}

export default JobRecruiterTabs;
